import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  FormControlLabel,
  Checkbox,
  Link,
  IconButton,
} from '@mui/material';
import { Google as GoogleIcon, Close as CloseIcon } from '@mui/icons-material';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../../context/AuthContext';
import TermsOfServiceDialog from './TermsOfServiceDialog';
import PrivacyPolicyDialog from './PrivacyPolicyDialog';

interface RegisterFormProps {
  onToggleMode: () => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onToggleMode }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<'coach' | 'athlete' | 'headCoach'>('athlete');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [showPrivacyDialog, setShowPrivacyDialog] = useState(false);
  const { signUp, signInWithGoogle, signInWithGoogleRedirect } = useAuth();
  
  // Check platform immediately (not in useEffect to avoid render delay)
  const platform = Capacitor.getPlatform();
  const isWeb = platform === 'web';
  
  // Log platform for debugging - MULTIPLE LOGS TO ENSURE VISIBILITY
  console.log('🟢🟢🟢 RegisterForm RENDERED - Platform:', platform, 'isWeb:', isWeb, '🟢🟢🟢');
  useEffect(() => {
    console.log('🟢 RegisterForm useEffect - Platform detected:', platform, 'isWeb:', isWeb);
    console.log('🟢 RegisterForm - Google Sign-In button will be:', isWeb ? 'VISIBLE' : 'HIDDEN');
  }, [platform, isWeb]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!acceptedTerms) {
      setError('You must accept the Terms of Service to create an account');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await signUp(email, password, displayName, role);
    } catch (error: any) {
      setError(error.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      const platform = Capacitor.getPlatform();
      
      // On web, add timeout to detect stuck popups
      if (platform === 'web') {
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Popup timeout - trying redirect instead')), 10000);
        });
        
        await Promise.race([
          signInWithGoogle(),
          timeoutPromise
        ]);
      } else if (platform === 'ios') {
        // iOS - show error (native not working)
        throw new Error('Google Sign-In is unavailable on iOS. Please use email/password sign-in.');
      } else {
        // Android - try web-based sign-in (should work in WebView)
        await signInWithGoogle();
      }
    } catch (error: any) {
      console.error('Google Sign-In failed:', error);
      
      // Handle errors based on platform
      const platform = Capacitor.getPlatform();
      
      if (platform === 'ios') {
        // iOS - show the error (shouldn't happen since button is hidden, but just in case)
        setError(error.message || 'Failed to sign in with Google. Please use email/password sign-in.');
      } else if (platform === 'web' || platform === 'android') {
        // Web and Android - try redirect as fallback if popup failed
        if (error.message?.includes('popup') || 
            error.message?.includes('Cross-Origin') || 
            error.message?.includes('blocked') ||
            error.message?.includes('timeout') ||
            error.message?.includes('cancelled')) {
          try {
            setError('Popup blocked or timed out. Redirecting to Google sign-in...');
            await signInWithGoogleRedirect();
            // Note: setLoading(false) will be handled by the redirect
            return;
          } catch (redirectError: any) {
            setError(redirectError.message || 'Failed to sign in with Google');
          }
        } else {
          setError(error.message || 'Failed to sign in with Google');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ 
      p: { xs: 3, sm: 4 }, 
      maxWidth: 400, 
      mx: 'auto', 
      mt: { xs: 2, sm: 4 },
      width: '100%',
      position: 'relative'
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1, textAlign: 'center' }}>
          Sign Up
        </Typography>
        <IconButton
          onClick={onToggleMode}
          disabled={loading}
          sx={{ 
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'text.secondary'
          }}
          aria-label="Close sign up"
        >
          <CloseIcon />
        </IconButton>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Display Name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          margin="normal"
          required
          disabled={loading}
        />
        
        <TextField
          fullWidth
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          margin="normal"
          required
          disabled={loading}
        />
        
        <FormControl fullWidth margin="normal" required>
          <InputLabel>Role</InputLabel>
          <Select
            value={role}
            label="Role"
            onChange={(e) => setRole(e.target.value as 'coach' | 'athlete' | 'headCoach')}
            disabled={loading}
          >
            <MenuItem value="athlete">Athlete</MenuItem>
            <MenuItem value="coach">Coach</MenuItem>
            <MenuItem value="headCoach">Head Coach</MenuItem>
          </Select>
        </FormControl>
        
        <TextField
          fullWidth
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          margin="normal"
          required
          disabled={loading}
        />
        
        <TextField
          fullWidth
          label="Confirm Password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          margin="normal"
          required
          disabled={loading}
        />
        
        <FormControlLabel
          control={
            <Checkbox
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              disabled={loading}
              required
            />
          }
          label={
            <Typography variant="body2">
              I agree to the{' '}
              <Link
                component="button"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setShowTermsDialog(true);
                }}
                sx={{ textDecoration: 'underline', cursor: 'pointer' }}
              >
                Terms of Service
              </Link>
              {' '}and{' '}
              <Link
                component="button"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setShowPrivacyDialog(true);
                }}
                sx={{ textDecoration: 'underline', cursor: 'pointer' }}
              >
                Privacy Policy
              </Link>
            </Typography>
          }
          sx={{ mt: 2, mb: 1 }}
        />
        
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 2, mb: 2 }}
          disabled={loading || !acceptedTerms}
        >
          {loading ? <CircularProgress size={24} /> : 'Sign Up'}
        </Button>

        {/* Google Sign-In enabled for web only, disabled on iOS and Android */}
        {isWeb && (
          <>
            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" color="text.secondary">
                OR
              </Typography>
            </Divider>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<GoogleIcon />}
              onClick={handleGoogleSignIn}
              disabled={loading}
              sx={{ mb: 2 }}
            >
              Sign up with Google
            </Button>
          </>
        )}
        
        <Button
          fullWidth
          variant="text"
          onClick={onToggleMode}
          disabled={loading}
          sx={{ mb: 1 }}
        >
          Already have an account? Sign in
        </Button>
        
        <Button
          fullWidth
          variant="outlined"
          onClick={onToggleMode}
          disabled={loading}
          color="secondary"
        >
          Cancel
        </Button>
      </Box>

      <TermsOfServiceDialog
        open={showTermsDialog}
        onClose={() => setShowTermsDialog(false)}
      />

      <PrivacyPolicyDialog
        open={showPrivacyDialog}
        onClose={() => setShowPrivacyDialog(false)}
      />
    </Paper>
  );
};

export default RegisterForm;
